/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from 'atoms';
import { VendorContractFormV2 } from 'componentsv2/forms';
import {
	searchVendorV2,
	searchVehicleTypesV2,
	searchRoutesV2,
	getVendorContractByIdV2,
} from 'store/vendorSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';

// Helper functions to transform API responses to dropdown format
const transformVendors = (rows) =>
	rows?.map((item) => ({
		value: item.id,
		label: item.vendor_name || item.name || '-',
	})) || [];

const transformVehicles = (rows) =>
	rows?.map((item) => ({
		value: item.id || item.vehicle_type_id || item.vehicle_type,
		label: item.vehicle_type_name || item.vehicle_type || item.name || item.label || '-',
	})) || [];

const transformProjects = (rows) =>
	rows?.map((item) => ({
		value: item.id,
		label: item.project_name || item.project_code || '-',
	})) || [];

const transformRoutes = (rows) =>
	rows?.map((item) => ({
		value: item.id || item.route_id,
		label: item.route_name || item.name || item.route_code || '-',
	})) || [];

// Helper function to transform week off days
const transformWeekOffDays = (weekOffDays) => {
	if (!weekOffDays) return [];
	if (typeof weekOffDays === 'string') {
		return weekOffDays.split(',');
	}
	return weekOffDays;
};

// Helper function to map contract data to form structure
const mapContractDataToForm = (contractData) => ({
	id: contractData.id,
	contract_id: contractData.contract_id,
	approval_status: contractData.approval_status,
	is_active: contractData.is_active,
	has_pending_edit: contractData.has_pending_edit ?? false,
	vendor_id: contractData.vendor_id,
	project_id: contractData.project_id,
	billing_type: contractData.billing_type,
	billing_subtype: contractData.billing_subtype,
	fixed_rate_per_day: contractData.fixed_rate_per_day,
	order_rates: contractData.order_rates || [],
	temperature_controlled: contractData.temperature_controlled,
	vehicle_type_id: contractData.vehicle_type_id,
	capacity_tonnes: contractData.capacity_tonnes,
	contract_date: contractData.contract_date,
	expiry_date: contractData.expiry_date,
	route_id: contractData.route_id,
	vehicle_cost: contractData.vehicle_cost,
	loader_cost: contractData.loader_cost,
	delivery_associate_cost: contractData.delivery_associate_cost,
	selected_no_of_vehicles: contractData.selected_no_of_vehicles,
	multiple_trips: contractData.multiple_trips,
	reporting_time: contractData.reporting_time,
	week_off_days: transformWeekOffDays(contractData.week_off_days),
	fixed_hours_per_day: contractData.fixed_hours_per_day,
	threshold_km_per_day: contractData.threshold_km_per_day,
	threshold_km_per_month: contractData.threshold_km_per_month,
	extra_charges_per_km: contractData.extra_charges_per_km ?? contractData.charges_per_km,
	extra_charges_per_hour: contractData.extra_charges_per_hour,
	other_expenses: contractData.other_expenses_type || contractData.other_expenses || '',
	other_other_expenses:
		contractData.other_expenses_value ?? contractData.other_other_expenses ?? '',
	signed_contract_file:
		contractData.document_info?.signed_contract_file?.document_key ||
		contractData.signed_contract ||
		null,
	vehicles: contractData.vehicles || [],
	vehicle_ids: contractData.vehicle_ids || [],
});

export default function CreateVendorContractPageV2() {
	const dispatch = useDispatch();
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(true);
	const [vendorsData, setVendorsData] = useState([]);
	const [vehicleData, setVehicleData] = useState([]);
	const [projectData, setProjectData] = useState([]);
	const [routeData, setRouteData] = useState([]);
	const { id } = useParams();
	const location = useLocation();

	// Detect create mode vs view mode
	const isCreateMode = location.pathname.endsWith('/add');
	const [isViewMode, setIsViewMode] = useState(Boolean(id) && !isCreateMode);

	// Fetch all dropdown data on mount
	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true);
			try {
				// Fetch vendors
				const vendorsResponse = await dispatch(
					searchVendorV2({
						filter_by: { is_active: true },
						pagination: { page: 1, per_page: 1000 },
					})
				);
				setVendorsData(transformVendors(vendorsResponse?.rows));

				// Fetch vehicle types
				const vehicleResponse = await dispatch(
					searchVehicleTypesV2({
						filter_by: {},
						limit: 1000,
					})
				);
				setVehicleData(transformVehicles(vehicleResponse?.rows));

				// Fetch projects
				const projectResponse = await dispatch(
					getProjectListV2({
						filter_by: {},
						pagination: { page: 1, per_page: 1000 },
					})
				);
				setProjectData(transformProjects(projectResponse?.rows));

				// Fetch routes
				const routeResponse = await dispatch(
					searchRoutesV2({
						filter_by: {},
						pagination: { page: 1, per_page: 1000 },
					})
				);
				setRouteData(transformRoutes(routeResponse?.rows));
			} catch (error) {
				console.error('Error fetching dropdown data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchAllData();
	}, [dispatch]);

	// Fetch vendor contract data when in view/edit mode
	useEffect(() => {
		const fetchVendorContract = async () => {
			if (!id) return;

			setLoading(true);
			try {
				// Get vendor_id from location state if available, otherwise use id
				const vendorId = location.state?.vendorId;
				const contractData = await dispatch(
					getVendorContractByIdV2({ id, vendor_id: vendorId })
				);
				if (contractData) {
					const mappedData = mapContractDataToForm(contractData);
					setFormData(mappedData);
				}
			} catch (error) {
				console.error('Error fetching vendor contract:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchVendorContract();
	}, [id, dispatch, location.state]);

	if (loading) {
		return <p>Loading....</p>;
	}

	return (
		<Box>
			<VendorContractFormV2
				isEditMode={Boolean(id)}
				isViewMode={isViewMode}
				setIsViewMode={setIsViewMode}
				formData={formData}
				vendorsData={vendorsData}
				vehicleData={vehicleData}
				projectData={projectData}
				routeData={routeData}
			/>
		</Box>
	);
}
